import { ReactComponent as ArrowIcon } from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as BinIcon } from '../../../assets/icons/TrashBin2.svg';
import React, { useState } from 'react';
import Button from '../../BaseComponents/Button';
import RangeChart from '../../Members/TestResults/PublishedTestResultsTable/RangeChart';
import RangeSummary from '../../Members/TestResults/PublishedTestResultsTable/RangeSummary';
import { BiomarkerRangeScale } from '../../BaseComponents/Biomarkers/BiomarkerRangeScale';
import './ShowBiomarkersTestResultsNodal.scss';
import BiomarkerHistoryChart from './BiomarkerHistoryChart';
import {
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO,
  AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO,
  HistoricalBiomarkerDataElement
} from '../../../types/apiGeneratedTypes';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { resolveRangeData, isBiomarkerActive } from '../../../utils/biomarkers';
import TestResultStatus from '../TestResultStatus';
import { formatInTimeZone } from 'date-fns-tz';
import { BiomarkerCalculationDisplay } from '../../common/BiomarkerCalculationDisplay';

interface SingleBiomarkerAlertProps {
  alertData:
  | AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO
  | AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO;
  openedAccordion: number | null;
  setOpenedBiomarker: (biomarkerIndex: number | null) => void;
  member_id: string;
  showDeleteButton?: boolean;
  onDeleteResult?: (resultId: number) => void;
  historyRefreshKey?: number;
}

const SingleBiomarkerAlert = ({
  alertData,
  openedAccordion,
  setOpenedBiomarker,
  member_id,
  showDeleteButton = false,
  onDeleteResult,
  historyRefreshKey = 0
}: SingleBiomarkerAlertProps) => {
  const [historicalData, setHistoricalData] = useState<HistoricalBiomarkerDataElement[]>([]);
  const { selectedTestResultMemberDOB, selectedTestResultMemberGender } = useSelector(
    (state: RootState) => state.alerts
  );

  const onOpenBiomarker = () => {
    alertData.biomarker.id === openedAccordion
      ? setOpenedBiomarker(null)
      : setOpenedBiomarker(alertData.biomarker.id!);
  };

  const rangeData = resolveRangeData(alertData?.biomarker, {
    dob: selectedTestResultMemberDOB,
    gender: selectedTestResultMemberGender
  });

  const getBiomarkerDisplayValue = (biomarker: any, result: any) => {
    if (biomarker.biomarker_type === 'BINARY') {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }

    if (biomarker.name.toLowerCase().includes('anti-nuclear')) {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }

    return `${result.value} ${biomarker.measurement_unit}`;
  };

  const isExpanded = alertData.biomarker.id === openedAccordion;
  const isBiomarkerDisabled = !isBiomarkerActive(alertData.biomarker);

  const handleDeleteClick = () => {
    if (alertData.result.id && onDeleteResult) {
      onDeleteResult(alertData.result.id);
    }
  };

  return (
    <div
      className={`single-biomarker-alert ${isExpanded
        ? 'opened-single-biomarker-alert'
        : ''
        }`}
    >
      <div className='single-biomarker-alert__header' onClick={onOpenBiomarker}>
        <div className='single-biomarker-alert__header-name-container'>
          <div className='single-biomarker-alert__header-name'>
            {alertData.biomarker.name}
            {isBiomarkerDisabled && (
              <span className='single-biomarker-alert__disabled-label'>
                (biomarker is disabled)
              </span>
            )}
          </div>
          {(alertData.biomarker as any).is_derived && (
            <div className='single-biomarker-alert__derived-tag'>derived</div>
          )}
        </div>
        <div className='value-and-arrow-container'>
          {alertData.biomarker.id !== openedAccordion && (
            <>
              <RangeChart
                biomarker={alertData.biomarker}
                value={alertData.result.value}
                scale={rangeData}
                width='120px'
                minWidth='120px'
              />
              <div className='range-summary-wrapper'>
                {(alertData.biomarker.biomarker_type === 'BINARY' || alertData.biomarker.name.toLowerCase().includes('anti-nuclear')) ? (
                  <div className='summary-container'>
                    <div className='biomarker-value-above' title={getBiomarkerDisplayValue(alertData.biomarker, alertData.result)}>
                      {getBiomarkerDisplayValue(alertData.biomarker, alertData.result)}
                    </div>
                    <RangeSummary
                      biomarker={alertData.biomarker}
                      value={alertData.result.value}
                      scale={rangeData}
                    />
                  </div>
                ) : alertData.biomarker.biomarker_type === 'UNITS' ? (
                  <div className='summary-container'>
                    <div className='biomarker-value-above' title={getBiomarkerDisplayValue(alertData.biomarker, alertData.result)}>
                      {getBiomarkerDisplayValue(alertData.biomarker, alertData.result)}
                    </div>
                  </div>
                ) : (
                  <RangeSummary
                    biomarker={alertData.biomarker}
                    value={alertData.result.value}
                    scale={rangeData}
                  />
                )}
              </div>
            </>
          )}
          <ArrowIcon
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
            className='single-biomarker-alert__header-arrow-icon'
          />
        </div>
      </div>
      <div className='single-biomarker-test-result-content'>
        <div className='single-biomarker-alert__status-container'>
          <TestResultStatus status={alertData.result.status} />
        </div>
        <div className='single-biomarker-alert__summary'>
          <div className='single-biomarker-alert__summary-label'>Summary:</div>
          <div>{alertData.result.summary}</div>
        </div>

        {(alertData.biomarker as any).calculated_value && (
          <BiomarkerCalculationDisplay
            resultValue={(alertData.biomarker as any).calculated_value}
            className='single-biomarker-alert__calculation-section'
            labelClassName='single-biomarker-alert__calculation-label'
            formulaClassName='single-biomarker-alert__calculation-formula'
          />
        )}
        <div className='single-biomarker-alert__current-value'>
          <div className='single-biomarker-alert__current-value-container'>
            <div className='single-biomarker-alert__current-value-label'>
              Current value
            </div>
            <div className='single-biomarker-alert__current-value-value'>
              <span className='single-biomarker-alert__current-value-value-number' title={getBiomarkerDisplayValue(alertData.biomarker, alertData.result)}>
                {getBiomarkerDisplayValue(alertData.biomarker, alertData.result)}
              </span>
              <RangeSummary
                biomarker={alertData.biomarker}
                value={alertData.result.value}
                scale={rangeData}
              />
            </div>
          </div>
          {(alertData.biomarker.biomarker_type !== 'BINARY' &&
            alertData.biomarker.biomarker_type !== 'UNITS' &&
            !alertData.biomarker.name.toLowerCase().includes('anti-nuclear')) && rangeData && (
              <BiomarkerRangeScale
                scale={rangeData}
                currentValue={alertData.result.value}
                biomarker={alertData.biomarker}
              />
            )}
        </div>
        {alertData.biomarker.biomarker_type !== 'BINARY' &&
          !alertData.biomarker.name.toLowerCase().includes('anti-nuclear') &&
          (alertData.biomarker.biomarker_type === 'UNITS' || rangeData) && (
            <div className='single-biomarker-alert__value-chart'>
              <div className='single-biomarker-alert__value-chart-label'>
                Historical value chart
              </div>
              {alertData.biomarker.id === openedAccordion && (
                <div className='single-biomarker-alert__historical-content'>
                  <div className='single-biomarker-alert__historical-chart'>
                    <BiomarkerHistoryChart
                      rangeData={
                        alertData.biomarker.biomarker_type === 'UNITS' ? null : rangeData
                      }
                      biomarker_id={alertData.biomarker.id!}
                      member_id={member_id}
                      onDataLoaded={setHistoricalData}
                      unit={alertData.biomarker.measurement_unit || undefined}
                      refreshKey={historyRefreshKey}
                    />
                  </div>
                  {historicalData.length > 0 && (
                    <div className='single-biomarker-alert__historical-table'>
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Value</th>
                            <th>Units</th>
                            {showDeleteButton && onDeleteResult && (
                              <th className='history-actions-header' />
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {[...historicalData].reverse().map((item) => (
                            <tr key={item.id}>
                              <td>
                                {formatInTimeZone(
                                  new Date(item.date),
                                  'UTC',
                                  'dd/MM/yyyy'
                                )}
                              </td>
                              <td>{item.value}</td>
                              <td>{alertData.biomarker.measurement_unit || '-'}</td>
                              {showDeleteButton && onDeleteResult && (
                                <td className='history-delete-cell'>
                                  <button
                                    type='button'
                                    className='history-delete-btn'
                                    aria-label='Delete result'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteResult(item.id);
                                    }}
                                  >
                                    <BinIcon className='history-delete-icon' />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        {isExpanded && showDeleteButton && onDeleteResult && (
          <Button
            variant='delete'
            text='Delete biomarker result'
            onClick={handleDeleteClick}
            backgroundColor='#ffffff'
            color='red'
          />
        )}
      </div>
    </div>
  );
};

export default SingleBiomarkerAlert;
